import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Button,
  Stack,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface Appraisal {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  deliverableType?: string;
  propertyName: string | null;
  hotelClass: string | null;
  country: string | null;
  marketName: string | null;
  propertyAddress: string | null;
}

export const AppraisalsPage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [appraisals, setAppraisals] = useState<Appraisal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch appraisals
  const fetchAppraisals = useCallback(async () => {
    try {
      setError(null);
      const response = await authenticatedFetch(`${API_URL}/api/appraisals-list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        setAppraisals(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch appraisals');
      }
    } catch (err) {
      console.error('Error fetching appraisals:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch appraisals');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    fetchAppraisals();
  }, [fetchAppraisals]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAppraisals();
  };

  const handleBreadcrumbClick = (event: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    event.preventDefault();
    navigate(path);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleView = (appraisalId: number) => {
    navigate(`/appraisals/${appraisalId}`);
  };

  const handleEdit = (appraisalId: number) => {
    navigate(`/appraisals/${appraisalId}/edit`);
  };

  const handleDelete = async (appraisalId: number) => {
    if (!window.confirm('Are you sure you want to delete this appraisal?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`${API_URL}/api/appraisals/${appraisalId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      // Refresh the list after successful deletion
      fetchAppraisals();
    } catch (err) {
      console.error('Error deleting appraisal:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete appraisal');
    }
  };

  const handleCreate = () => {
    navigate('/appraisals/new');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getHotelClassChip = (hotelClass: string | null) => {
    if (!hotelClass) return null;
    
    const colorMap: Record<string, 'error' | 'warning' | 'success' | 'info' | 'default'> = {
      'Luxury': 'error',
      'Upper Upscale': 'warning',
      'Upscale': 'success',
      'Upper Midscale': 'info',
      'Midscale': 'default',
      'Economy': 'default',
    };

    return (
      <Chip 
        label={hotelClass} 
        size="small" 
        color={colorMap[hotelClass] || 'default'}
        variant="outlined"
      />
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <Link
          color="inherit"
          href="/"
          onClick={(e) => handleBreadcrumbClick(e, '/')}
          sx={{ 
            cursor: 'pointer',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          Home
        </Link>
        <Typography color="text.primary">Appraisals</Typography>
      </Breadcrumbs>

      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                mb: 1
              }}
            >
              Appraisals
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
            >
              Manage and view property appraisals
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Tooltip title="Refresh">
              <IconButton 
                onClick={handleRefresh} 
                disabled={refreshing}
                sx={{ 
                  border: 1, 
                  borderColor: 'divider',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <RefreshIcon sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{ height: 40 }}
            >
              New Appraisal
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Main Content Area */}
      <Paper 
        elevation={1} 
        sx={{ 
          backgroundColor: 'background.paper' 
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : appraisals.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <HomeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Appraisals Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You haven't created any appraisals yet.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
            >
              Create Your First Appraisal
            </Button>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Property</TableCell>
                    <TableCell>Hotel Class</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appraisals
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((appraisal) => (
                    <TableRow key={appraisal.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {appraisal.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {appraisal.propertyName ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {appraisal.propertyName}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No property name
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {getHotelClassChip(appraisal.hotelClass)}
                      </TableCell>
                      <TableCell>
                        {(appraisal.marketName || appraisal.country) ? (
                          <Box>
                            {appraisal.marketName && (
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {appraisal.marketName}
                              </Typography>
                            )}
                            {appraisal.country && (
                              <Typography variant="caption" color="text.secondary">
                                {appraisal.country}
                              </Typography>
                            )}
                            {appraisal.propertyAddress && (
                              <Tooltip title={appraisal.propertyAddress}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                  <LocationOnIcon sx={{ fontSize: 14 }} color="action" />
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary"
                                    sx={{ 
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: 200
                                    }}
                                  >
                                    {appraisal.propertyAddress}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No location data
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(appraisal.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(appraisal.updatedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => handleView(appraisal.id)}
                              sx={{ color: 'primary.main' }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(appraisal.id)}
                              sx={{ color: 'warning.main' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(appraisal.id)}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={appraisals.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </Container>
  );
};